import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import { getEcosystems } from '@frontend/api-clients/ecosystemsClient';
import {
  fetchDashboardOverview,
  fetchProjectsForEcosystem,
  fetchAdminProjectsCreateScope,
} from '@frontend/api-clients/dashboardClient';
import type { EcosystemDefinition, EcosystemProject, User } from '@frontend/types';
import {
  canSeeEcosystemAdmin,
  canSeeProjectAdmin,
  canSeeRealmAdmin,
  canSeeSystemAdmin,
} from '@frontend/utils/adminHubAccess';
import type { DashboardOverviewResponse, NodeSelection } from './types';
import DashboardSidebar from './sidebar/DashboardSidebar';
import MainPanel from './mainpanel/MainPanel';
import type { HotStackUiPhase } from '@frontend/types/hotloading';
import {
  fetchHotloadState,
  postHotloadState,
  RuntimeApiError,
  type HotloadState,
  type RuntimeModeResponse
} from '@frontend/api-clients/runtimeModeClient';
import {
  applyRuntimePayloadState,
  canSwitchRouteToHot,
  canToggleStack,
  stackClickTargetsRefresh,
  type HotloadingUiState
} from '@frontend/features/hotloading/runtimeHotloadingState';

function formatRuntimeError(err: unknown, fallback: string): string {
  if (err instanceof RuntimeApiError) {
    if (err.status === 401) {
      return 'Portal session expired. Sign in again (hotloading does not use a separate token).';
    }
    return err.message;
  }
  return err instanceof Error ? err.message : fallback;
}

interface DashboardPageProps {
  user: User | null;
}

export default function DashboardPage({ user }: DashboardPageProps) {
  const [overview, setOverview] = useState<DashboardOverviewResponse | null>(null);
  const [runtimeEnvironment, setRuntimeEnvironment] = useState<'testing' | 'staging' | 'production'>(
    window.location.origin.includes('testing.')
      ? 'testing'
      : window.location.origin.includes('staging.')
        ? 'staging'
        : 'production'
  );
  const [showHotloadingControl, setShowHotloadingControl] = useState(false);
  const [stackUiPhase, setStackUiPhase] = useState<HotStackUiPhase>('down');
  const [routeMode, setRouteMode] = useState<'hot' | 'parity'>('parity');
  const [routeToggleEnabled, setRouteToggleEnabled] = useState(false);
  const [routeToHotAllowed, setRouteToHotAllowed] = useState(false);
  const [routeBusy, setRouteBusy] = useState(false);
  const [stackBusy, setStackBusy] = useState(false);
  const [hotloadingError, setHotloadingError] = useState('');
  const stackUiPhaseRef = useRef<HotStackUiPhase>('down');
  const [ecosystemDefs, setEcosystemDefs] = useState<EcosystemDefinition[]>([]);
  const [projectsByEcosystem, setProjectsByEcosystem] = useState<Record<string, EcosystemProject[]>>({});
  const [expandedEcosystems, setExpandedEcosystems] = useState<Record<string, boolean>>({});
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [selected, setSelected] = useState<NodeSelection>({ kind: 'ecosystem', ecosystemId: '' });
  /** Maintainers-only (or similar) provision eligibility surfaced even when scoped admin hubs are unavailable. */
  const [projectProvisionAdminEligible, setProjectProvisionAdminEligible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const applyRuntimePayload = useCallback(
    (data: RuntimeModeResponse, options: { preserveTransitional?: boolean; routeOnly?: boolean; clearHotloadingError?: boolean } = {}) => {
      const prev: HotloadingUiState = {
        runtimeEnvironment,
        showHotloadingControl,
        stackUiPhase: stackUiPhaseRef.current,
        routeMode,
        routeToggleEnabled,
        routeToHotAllowed,
        hotloadingError
      };
      const next = applyRuntimePayloadState(prev, data, {
        ...options,
        currentStackUiPhase: stackUiPhaseRef.current
      });
      setRuntimeEnvironment(next.runtimeEnvironment);
      setShowHotloadingControl(next.showHotloadingControl);
      setRouteMode(next.routeMode);
      setRouteToggleEnabled(next.routeToggleEnabled);
      setRouteToHotAllowed(next.routeToHotAllowed);
      setHotloadingError(next.hotloadingError);
      if (!options.routeOnly) {
        setStackUiPhase(next.stackUiPhase);
        stackUiPhaseRef.current = next.stackUiPhase;
      }
    },
    [
      runtimeEnvironment,
      showHotloadingControl,
      routeMode,
      routeToggleEnabled,
      routeToHotAllowed,
      hotloadingError
    ]
  );

  const accessibleEcosystems = useMemo(() => {
    if (!overview?.ecosystems) {
      return [];
    }

    return Object.keys(overview.ecosystems)
      .filter((ecoId) => overview.ecosystems[ecoId]?.accessible)
      .sort((a, b) => {
        const labelA = ecosystemDefs.find((e) => e.id === a)?.displayName || a;
        const labelB = ecosystemDefs.find((e) => e.id === b)?.displayName || b;
        return labelA.localeCompare(labelB);
      });
  }, [overview, ecosystemDefs]);

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      setError('');

      try {
        const token = authClient.getToken();
        if (!token) {
          throw new Error('You must be signed in to load the dashboard.');
        }

        const defs = await getEcosystems(token);
        setEcosystemDefs(defs);

        const overviewData = await fetchDashboardOverview(token);
        setOverview(overviewData);

        try {
          const runtimeData = await fetchHotloadState(token);
          applyRuntimePayload(runtimeData);
        } catch (runtimeErr) {
          if (window.location.origin.includes('testing.')) {
            setShowHotloadingControl(true);
            setHotloadingError(
              runtimeErr instanceof Error
                ? runtimeErr.message
                : 'Could not read runtime mode. Sign in again or check portal-backend logs.'
            );
          }
        }

        const ecoIds = Object.keys(overviewData.ecosystems || {}).filter(
          (ecoId) => overviewData.ecosystems[ecoId]?.accessible
        );

        const projectLoads = await Promise.all(
          ecoIds.map(async (ecoId) => {
            try {
              const projects = await fetchProjectsForEcosystem(ecoId, token);
              return [ecoId, projects] as const;
            } catch {
              return [ecoId, []] as const;
            }
          })
        );

        const nextProjectsByEco: Record<string, EcosystemProject[]> = {};
        const nextExpanded: Record<string, boolean> = {};
        for (const [ecoId, projects] of projectLoads) {
          nextProjectsByEco[ecoId] = [...projects];
          nextExpanded[ecoId] = true;
        }
        setProjectsByEcosystem(nextProjectsByEco);
        setExpandedEcosystems(nextExpanded);

        try {
          try {
            const body = (await fetchAdminProjectsCreateScope(token)) as { eligible?: boolean };
            setProjectProvisionAdminEligible(
              Boolean(body.eligible && !canSeeEcosystemAdmin(user) && !canSeeSystemAdmin(user))
            );
          } catch {
            setProjectProvisionAdminEligible(false);
          }
        } catch {
          setProjectProvisionAdminEligible(false);
        }

        if (ecoIds.length > 0) {
          setSelected({ kind: 'ecosystem', ecosystemId: ecoIds[0] });
        }
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [user, applyRuntimePayload]);

  useEffect(() => {
    if (!showHotloadingControl) {
      return undefined;
    }

    const token = authClient.getToken();
    if (!token) {
      return undefined;
    }

    const pollTransitional = stackUiPhase === 'loading' || stackUiPhase === 'unloading';
    const intervalMs = pollTransitional ? 2000 : 8000;

    const refreshRuntime = async () => {
      if (stackBusy || routeBusy) {
        return;
      }
      try {
        const data = await fetchHotloadState(token);
        applyRuntimePayload(data, { preserveTransitional: pollTransitional });
      } catch {
        // Ignore transient poll errors.
      }
    };

    const interval = window.setInterval(refreshRuntime, intervalMs);
    const onRefreshEvent = () => {
      void refreshRuntime();
    };
    window.addEventListener('hotloading:refresh-runtime', onRefreshEvent);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('hotloading:refresh-runtime', onRefreshEvent);
    };
  }, [showHotloadingControl, stackUiPhase, stackBusy, routeBusy, applyRuntimePayload]);

  const userName = user?.name || user?.username || 'Unknown user';
  const environmentLabel = runtimeEnvironment === 'testing'
    ? 'Environment: Testing'
    : runtimeEnvironment === 'staging'
      ? 'Environment: Staging'
      : 'Environment: Production';

  const goHome = () => setSelected({ kind: 'ecosystem', ecosystemId: accessibleEcosystems[0] || '' });

  const onHotStackToggle = async () => {
    if (stackBusy || routeBusy || stackUiPhase === 'loading' || stackUiPhase === 'unloading') {
      return;
    }

    const token = authClient.getToken();
    if (!token) {
      setHotloadingError('You must be signed in.');
      return;
    }

    const goingUp = stackClickTargetsRefresh({
      stackUiPhase,
      routeToHotAllowed
    });
    if (!goingUp && !canToggleStack({ routeMode, stackUiPhase })) {
      setHotloadingError('Switch route to parity before stopping the hot stack.');
      return;
    }

    setStackBusy(true);
    setHotloadingError('');
    setStackUiPhase(goingUp ? 'loading' : 'unloading');
    stackUiPhaseRef.current = goingUp ? 'loading' : 'unloading';

    const targetState: HotloadState = goingUp ? 'hotStandby' : 'parityIdle';

    try {
      const data = await postHotloadState(token, targetState);
      applyRuntimePayload(data, { clearHotloadingError: true });
    } catch (err) {
      setHotloadingError(formatRuntimeError(err, 'Hot stack action failed.'));
      setStackUiPhase(goingUp ? 'down' : 'up');
      stackUiPhaseRef.current = goingUp ? 'down' : 'up';
    } finally {
      setStackBusy(false);
    }
  };

  const onHotRouteToggle = async () => {
    if (routeBusy || stackBusy) {
      return;
    }
    if (!routeToggleEnabled) {
      setHotloadingError('Route toggle is disabled while the hot stack is down. Use Hot Stack Up first.');
      return;
    }

    const token = authClient.getToken();
    if (!token) {
      setHotloadingError('You must be signed in.');
      return;
    }

    const nextMode = routeMode === 'hot' ? 'parity' : 'hot';
    if (!canSwitchRouteToHot(routeToHotAllowed, nextMode)) {
      setHotloadingError('Hot stack is not healthy yet. Use Stack up/refresh first, then route to hot.');
      return;
    }

    const targetState: HotloadState = nextMode === 'hot' ? 'hotRouted' : 'hotStandby';

    setRouteBusy(true);
    setHotloadingError('');

    try {
      const data = await postHotloadState(token, targetState);
      setRouteMode(nextMode);
      applyRuntimePayload(data, { routeOnly: true, clearHotloadingError: true });
    } catch (err) {
      setHotloadingError(formatRuntimeError(err, 'Failed to switch route.'));
    } finally {
      setRouteBusy(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="container dashboard-container dashboard-container-wide">
        {loading ? <p className="ecosystem-description">Loading dashboard...</p> : null}
        {error ? <div className="settings-message settings-error">{error}</div> : null}
        {hotloadingError && !showHotloadingControl ? (
          <div className="settings-message settings-error">{hotloadingError}</div>
        ) : null}

        {!loading && !error ? (
          <div className={`tree-dashboard-layout ${sidebarHidden ? 'sidebar-hidden' : ''}`}>
            <DashboardSidebar
              userName={userName}
              environmentLabel={environmentLabel}
              showHotloadingControl={showHotloadingControl}
              stackUiPhase={stackUiPhase}
              routeMode={routeMode}
              routeToggleEnabled={routeToggleEnabled}
              routeToHotAllowed={routeToHotAllowed}
              routeBusy={routeBusy}
              stackBusy={stackBusy}
              hotloadingError={hotloadingError}
              sidebarHidden={sidebarHidden}
              selected={selected}
              accessibleEcosystems={accessibleEcosystems}
              ecosystemDefs={ecosystemDefs}
              projectsByEcosystem={projectsByEcosystem}
              expandedEcosystems={expandedEcosystems}
              onToggleSidebar={() => setSidebarHidden((value) => !value)}
              onSelectHome={goHome}
              onSelectSystemAdmin={() => setSelected({ kind: 'admin-system' })}
              onSelectRealmAdmin={() => setSelected({ kind: 'admin-realm' })}
              onSelectEcosystemAdmin={() => setSelected({ kind: 'admin-ecosystem' })}
              onSelectProjectAdmin={() => setSelected({ kind: 'admin-project' })}
              onSelectSettings={() => setSelected({ kind: 'ecosystem-settings' })}
              onSelectFeedback={() => setSelected({ kind: 'feedback' })}
              onSelectEcosystem={(ecosystemId) => setSelected({ kind: 'ecosystem', ecosystemId })}
              onSelectProject={(ecosystemId, projectId) => setSelected({ kind: 'project', ecosystemId, projectId })}
              onToggleEcosystemExpanded={(ecosystemId) => setExpandedEcosystems((prev) => ({ ...prev, [ecosystemId]: !(prev[ecosystemId] ?? true) }))}
              onHotStackToggle={onHotStackToggle}
              onHotRouteToggle={onHotRouteToggle}
              onLogout={() => authClient.logout()}
              canSeeSystemAdmin={canSeeSystemAdmin(user)}
              canSeeRealmAdmin={canSeeRealmAdmin(user)}
              canSeeEcosystemAdmin={canSeeEcosystemAdmin(user)}
              canSeeProjectAdmin={canSeeProjectAdmin(user, projectProvisionAdminEligible)}
            />

            <MainPanel
              user={user}
              projectAdminEligible={projectProvisionAdminEligible}
              selected={selected}
              ecosystemDefs={ecosystemDefs}
              projectsByEcosystem={projectsByEcosystem}
              overview={overview}
              onGoHome={goHome}
              onSelectProject={(ecosystemId, projectId) => setSelected({ kind: 'project', ecosystemId, projectId })}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
